import { t } from 'ttag'
import { useMemo, useState, useEffect, useContext } from 'react'
import { useDispatch } from 'react-redux'
import isFunction from 'lodash/isFunction'

import { NAVIANCEBLUE, SLB, PG } from '@h2/components/colors'
import { Heading } from '@h2/components/typography/heading'
import { Text } from '@h2/components/typography/text'
import { Field } from '@h2/components/inputs/field'
import { Select } from '@h2/components/inputs/select'
import { Alert } from '@h2/components/display/alert'
import { useForm } from '@h2/common/components/hooks/useForm'
import { Link } from '@h2/common/components/link'
import { Card } from '@h2/common/components/card'
import { openSigninDialog } from '@main/actions/signinOrRegisterDialog'
import { Globals } from '@h2/globals'

import { props } from './props'
import { dobForm as Form } from './components'

import { Create } from '../highlights/create'
import { Navigator } from '../highlights/navigator'
import { Controls } from '../controls'
import { Submit } from '../submit'

const BirthDate = ({
    inDialog,
    properties,
    isValidating,
    validate,
    formErrors,
    serverErrorMsg,
    customDetourHandler
}) => {
    const dispatch = useDispatch()
    const { isNavianceUser } = useContext(Globals)
    const isExplorer = useMemo(() => properties.isExplorer ?? true, [properties.isExplorer])
    const { error, handleOnSubmit, setValidation, setValue } = useForm()
    const [selectedMonth, setSelectedMonth] = useState('')
    const [selectedDate, setSelectedDate] = useState('')
    const [selectedYear, setSelectedYear] = useState('')
    const [isNextEnabled, setIsNextEnabled] = useState(false)
    const getDaysInMonth = (month, year) => {
        if (month === undefined || year === undefined) {
            return 0;
        }

        return new Date(year, month, 0).getDate();
    };
    const months = Array(12).fill().map((_, index) => index + 1);
    const years = Array(100).fill().map((_, index) => new Date().getFullYear() - index);
    const days = Array(getDaysInMonth(selectedMonth, selectedYear)).fill().map((_, index) => index + 1);
    const onSubmit = () => {
        validate({ noEmail: false, isSSO: false, birthDate: `${selectedMonth}-${selectedDate}-${selectedYear}` })
    }

    useEffect(() => {
        setValue('date', '')
        setSelectedDate('')
    }, [selectedMonth, setValue])

    useEffect(() => {
        if (formErrors) {
            setValidation(formErrors)
        }
    }, [formErrors, setValidation])

    useEffect(() => {
        setIsNextEnabled(selectedMonth && selectedDate && selectedYear)
    }, [selectedMonth, selectedDate, selectedYear])

    const detourLinkProps = useMemo(
        () => ({
            onClick:
                !inDialog && !isFunction(customDetourHandler)
                    ? undefined
                    : event => {
                        event.preventDefault()

                        if (isFunction(customDetourHandler)) {
                            customDetourHandler()
                        } else {
                            dispatch(openSigninDialog('signIn'))
                        }
                    }
        }),
        [dispatch, inDialog, customDetourHandler]
    )
    const headingTxt = `Select your date of birth.`
    const padWithZero = (number) => number.toString().padStart(2, '0');

    return (
        <Card useDetour inDialog={inDialog}>
            <Card.Highlight color={isNavianceUser ? NAVIANCEBLUE : isExplorer ? PG : SLB}>
                {isExplorer ? (
                    <Create isNamePanel type={isNavianceUser ? 'navianceUser' : 'unknown'} />
                ) : (
                    <Navigator />
                )}
            </Card.Highlight>
            <Card.Content>
                <Form onSubmit={handleOnSubmit(onSubmit)}>
                    {serverErrorMsg && (
                        <Alert
                            type="error"
                            message={serverErrorMsg}
                            margin="0 0 15px 0"
                            width="100%"
                        />
                    )}
                    <Heading level="2" align="center">
                        {headingTxt}
                    </Heading>
                    <Text align="center">
                    {t`Additional steps may be required to create your account.`}
                    </Text>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Field
                            isRequired
                            width="30%"
                            label={t`Month`}
                            desc={error.name}
                            status={error.name ? 'error' : 'default'}>
                            <Select
                                items={months}
                                value={selectedMonth}
                                onChange={event => {
                                    const paddedMonth = padWithZero(event.target.value);

                                    setSelectedMonth(paddedMonth);
                                    setValue('month', paddedMonth);
                                }}
                                placeholder="Select Month"
                            />
                        </Field>
                        <Field
                            isRequired
                            width="30%"
                            label={t`Day`}
                            desc={error.name}
                            status={error.name ? 'error' : 'default'}>
                            <Select
                                items={days}
                                value={selectedDate}
                                onChange={event => {
                                    const paddedDate = padWithZero(event.target.value);

                                    setSelectedDate(paddedDate);
                                    setValue('date', paddedDate);
                                }}
                                placeholder="Select Date"
                                isDisabled={!selectedMonth}
                            />
                        </Field>
                        <Field
                            isRequired
                            width="30%"
                            label={t`Year`}
                            desc={error.name}
                            status={error.name ? 'error' : 'default'}>
                            <Select
                                items={years}
                                value={selectedYear}
                                onChange={event => {
                                    setSelectedYear(event.target.value)
                                    setValue('year', event.target.value)
                                }}
                                placeholder="Select Year"
                            />
                        </Field>
                    </div>
                    <Controls useControls>
                        <Submit
                            text={t`Next`}
                            isLoading={isValidating}
                            isDisabled={!isNextEnabled || Boolean(serverErrorMsg)}
                        />
                    </Controls>
                </Form>
            </Card.Content>
            {isExplorer && (
                <Card.Detour>
                    <Text>
                        {t`Already have an account?`}{' '}
                        <Link href="/signin" {...detourLinkProps}>{t`Sign in here`}</Link>
                    </Text>
                </Card.Detour>
            )}
        </Card>
    )
}

BirthDate.propTypes = { ...props }

export { BirthDate }